/**
 * Slow Rib — Sponsor Placement Components
 *
 * Three zones per the design spec:
 *   <FieldSponsorBug>       — corner bug on the live video (§7.1)
 *   <LowerThirdSponsor>     — full-width band beneath the stage (§7.2)
 *   <SeasonSponsorCard>     — sidebar card on team/game pages (§7.3)
 */

import React, { useState, useEffect } from 'react';
import { mediaUrl } from '../utils/mediaUrl';
import api from '../api';

export interface Sponsor {
  id:              string;
  sponsor_id:      string;
  placement:       'field_bug' | 'lower_third' | 'season_card' | 'title';
  sponsor_name:    string;
  sponsor_tagline?: string;
  sponsor_logo?:   string;
  website_url?:    string;
}

// ── Field sponsor bug — bottom-right of video frame ───────────────────────────
export const FieldSponsorBug: React.FC<{ sponsor: Sponsor }> = ({ sponsor }) => (
  <div className="field-sponsor-bug">
    {sponsor.sponsor_logo ? (
      <img
        src={mediaUrl(sponsor.sponsor_logo)}
        alt={sponsor.sponsor_name}
        className="fs-logo"
      />
    ) : (
      <div>
        <div className="fs-meta">Sponsored by</div>
        <div className="fs-name">{sponsor.sponsor_name}</div>
      </div>
    )}
  </div>
);

// ── Lower-third — full-width band beneath the stage ───────────────────────────
export const LowerThirdSponsor: React.FC<{ sponsor: Sponsor }> = ({ sponsor }) => (
  <div className="lower-third-sponsor">
    <div>
      <div className="lt-eyebrow">Game Day Sponsor</div>
      <div className="lt-name">{sponsor.sponsor_name}</div>
      {sponsor.sponsor_tagline && (
        <div className="lt-tagline">{sponsor.sponsor_tagline}</div>
      )}
    </div>
    {sponsor.sponsor_logo && (
      <img
        src={mediaUrl(sponsor.sponsor_logo)}
        alt={sponsor.sponsor_name}
        className="lt-logo"
      />
    )}
  </div>
);

// ── Season sponsor card — sidebar on team/game pages ──────────────────────────
export const SeasonSponsorCard: React.FC<{ sponsor: Sponsor }> = ({ sponsor }) => (
  <div className="season-sponsor-card">
    <div className="ss-head">Season Sponsor</div>
    <div className="ss-body">
      <div className="ss-logo-box">
        {sponsor.sponsor_logo
          ? <img src={mediaUrl(sponsor.sponsor_logo)} alt={sponsor.sponsor_name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 10 }} />
          : sponsor.sponsor_name.charAt(0)}
      </div>
      <div className="ss-name">{sponsor.sponsor_name}</div>
      {sponsor.sponsor_tagline && <p className="ss-desc">{sponsor.sponsor_tagline}</p>}
      {sponsor.website_url && (
        <a
          href={sponsor.website_url}
          target="_blank"
          rel="noopener noreferrer"
          className="ss-cta"
        >
          Visit website <i className="ti ti-external-link" style={{ fontSize: 13 }} />
        </a>
      )}
    </div>
  </div>
);

// ── useSponsorPlacements hook — fetches sponsors for a game ───────────────────

export function useSponsorPlacements(gameId?: string) {
  const [placements, setPlacements] = useState<Sponsor[]>([]);

  useEffect(() => {
    if (!gameId) return;
    api.get(`/sponsors/for-game/${gameId}`)
      .then(r => setPlacements(r.data))
      .catch(() => {});
  }, [gameId]);

  const fieldBug   = placements.find(p => p.placement === 'field_bug');
  const lowerThird = placements.find(p => p.placement === 'lower_third');
  const seasonCard = placements.find(p => p.placement === 'season_card');

  return { fieldBug, lowerThird, seasonCard };
}

// ── useTeamSponsorPlacements hook — fetches sponsors for a team ───────────────

export function useTeamSponsorPlacements(teamId?: string) {
  const [placements, setPlacements] = useState<Sponsor[]>([]);

  useEffect(() => {
    if (!teamId) return;
    api.get(`/sponsors/for-team/${teamId}`)
      .then(r => setPlacements(r.data))
      .catch(() => {});
  }, [teamId]);

  const seasonCard = placements.find(p => p.placement === 'season_card');
  const fieldBug   = placements.find(p => p.placement === 'field_bug');

  return { seasonCard, fieldBug };
}
